import { Box, Typography } from '@mui/material';
import { Loader } from '@/components/loader/loader';
import { ATTESTATION_COLORS } from '@/components/ui';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { SvgDonut, ChartLegend } from '@/components/ds/charts';

interface AllocationPieChartProps {
  data: {
    allocated: number;
    unallocated: number;
  };
  isLoading?: boolean;
  currency?: string;
  isCostChart?: boolean;
}

export const AllocationPieChart = ({
  data,
  isLoading = false,
  currency = 'USD',
  isCostChart = false,
}: AllocationPieChartProps) => {

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={300}>
        <Loader />
      </Box>
    );
  }

  const total = data.allocated + data.unallocated;

  if (total === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={300}>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Box>
    );
  }

  const chartData = [
    {
      id: 0,
      value: data.allocated,
      label: 'Allocated',
      color: ATTESTATION_COLORS.confirmed,
    },
    {
      id: 1,
      value: data.unallocated,
      label: 'Unallocated',
      color: ATTESTATION_COLORS.pending,
    },
  ];

  const formatValue = (value: number) => {
    if (isCostChart) {
      return formatCurrencyNoUnit(value);
    }
    return value.toLocaleString();
  };

  const formatPercentage = (value: number) => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return `${formatCurrencyNoUnit(percentage)}%`;
  };

  const allocatedPct =
    total > 0 ? Math.round((data.allocated / total) * 100) : 0;

  const donutData = chartData.map((item) => ({
    v: total > 0 ? (item.value / total) * 100 : 0,
    c: item.color,
    l: item.label,
    tv: `${formatValue(item.value)} (${formatPercentage(item.value)})`,
  }));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 2,
      }}>
      <SvgDonut
        data={donutData}
        centerTop="Allocated"
        centerBot={`${allocatedPct}%`}
      />
      <Box sx={{ width: '100%', maxWidth: 280, mt: 1.5 }}>
        <ChartLegend
          items={chartData.map((item) => ({
            color: item.color,
            name: item.label,
            value: formatValue(item.value),
          }))}
        />
      </Box>
    </Box>
  );
};
